<script lang="ts">
  // Detail mode per INTERFACE-SPECIFICATION.md §6.
  //
  // Single jurisdiction-product. Renders the cube table; users with
  // proposer:rates (or admin) see the cube editor — click a cell to
  // stage an edit, see the staged-changes panel auto-save through
  // the proxy Worker, and watch the Marketing-Surface Preview update
  // live. Users without the proposer role see read-only.
  //
  // Submission (the Submit for Review button) lives in commit 6
  // (SubmitForReview component).

  import { onMount } from 'svelte';
  import { cachedCatalogue, fetchCatalogue, getInEffectVersion, catalogueState } from '../lib/catalogue-store.svelte';
  import { findProduct } from '../lib/products';
  import { surfaceState } from '../lib/surface.svelte';
  import { identity } from '../lib/identity.svelte';
  import { adoptDraftFromServer, beginEditing, loadFromSession, stagedEditsState } from '../lib/staged-edits.svelte';
  import type { DraftSummary, Jurisdiction, ProductId } from '../lib/types';
  import { api, ApiError } from '../lib/api';
  import CubeTable from './CubeTable.svelte';
  import CubeEditor from './CubeEditor.svelte';
  import MarketingSurfacePreview from './MarketingSurfacePreview.svelte';
  import LoanAmountRangeEditor from './LoanAmountRangeEditor.svelte';
  import LoanPurposesEditor from './LoanPurposesEditor.svelte';
  import FeesEditor from './FeesEditor.svelte';
  import TermsEditor from './TermsEditor.svelte';
  import DraftResumePrompt from './DraftResumePrompt.svelte';
  import { applyStagedEditsToCatalogue } from '../lib/staged-edits';
  import { formatLocalDate, formatAmount } from '../lib/formatters';
  import { dimensions } from '../lib/dimensions';

  interface Props {
    jurisdiction: Jurisdiction;
    product: ProductId;
  }

  const { jurisdiction, product }: Props = $props();

  let channel = $state<string>('hLoan');

  const descriptor = $derived(findProduct(jurisdiction, product));
  const catalogue = $derived(cachedCatalogue(jurisdiction, product));
  const inEffect = $derived(getInEffectVersion(catalogueState.manifest, jurisdiction, product));

  const canEdit = $derived(identity.affordances.canProposeRates);
  const canEditAmounts = $derived(identity.affordances.canProposeAmounts);
  const canEditPurposes = $derived(identity.affordances.canProposePurposes);
  const canEditFees = $derived(identity.affordances.canProposeFees);
  const canEditTerms = $derived(identity.affordances.canProposeTerms);

  // Re-fetch on mount and on jurisdiction/product change so the
  // proposer never sees a stale cube — the parent Catalogue.svelte
  // route stays mounted across mode changes (matrix → detail and
  // back), so its onMount only fires on the first entry into
  // /catalogue/*. Without this, a merge that happens after the user
  // has navigated through Catalogue once is invisible until they
  // leave and return to the whole route. fetchCatalogue ignores its
  // cache for unhashed live reads (see catalogue-store.svelte), so
  // this is always a fresh KV round-trip.
  $effect(() => {
    void fetchCatalogue(jurisdiction, product);
  });

  // Initialise the staged-edits session for any user who holds at
  // least one proposer role on this surface — not just rates. Without
  // this, a purposes-only or amounts-only proposer accumulates edits
  // in the store's category buckets but draftSlug stays null, so the
  // auto-save snapshot returns null (no save) and SubmitForReview's
  // handleSubmit bails on `if (!stagedEditsState.draftSlug) return`.
  // The store applies value-type mutual exclusion regardless, so
  // initialising eagerly does not let a viewer-only account
  // accumulate state for an edit category they cannot submit — every
  // stage* call still gates on the per-role checks server-side.
  const canEditAny = $derived(canEdit || canEditAmounts || canEditPurposes || canEditFees || canEditTerms);

  // Surface an existing server-side draft for (jurisdiction, product)
  // when the SPA's local sessionStorage carries no in-flight state.
  // Without this prompt, a draft from a prior session (closed browser
  // tab, identity-switch, slug-collision-era pollution) silently
  // becomes the baseline for the next saveDraft. The
  // proposed/au-unsecured-personal-loan-2026-05-21-001 incident — a
  // rate-only proposal that silently merged a prior session's
  // establishment_fee_override removal — was caused by exactly that
  // shape. Slug uniqueness (PR before this one) closes the most
  // common collision pattern; this prompt closes the residual gap.
  let pendingDraft = $state<DraftSummary | null>(null);
  let draftPromptBusy = $state<boolean>(false);
  let draftLookupError = $state<string | null>(null);

  async function detectExistingDraft(): Promise<void> {
    if (!canEditAny || !identity.email) return;
    try {
      const { active } = await api.listDrafts();
      const matches = active.filter(
        (d) => d.jurisdiction === jurisdiction && d.product === product
      );
      if (matches.length === 0) return;
      // Most-recently edited wins if several active drafts exist for
      // the same product (defence against pre-fix slug-collision
      // history). The operator can still see the slug on the prompt.
      matches.sort((a, b) =>
        a.last_edited_at < b.last_edited_at ? 1 : -1
      );
      pendingDraft = matches[0]!;
    } catch (err) {
      draftLookupError = err instanceof ApiError
        ? `${err.code}: ${err.message}`
        : err instanceof Error
        ? err.message
        : 'draft_lookup_failed';
    }
  }

  async function handleResume(): Promise<void> {
    if (!pendingDraft || !catalogue) return;
    draftPromptBusy = true;
    try {
      await adoptDraftFromServer(pendingDraft.draft_slug, catalogue);
      pendingDraft = null;
    } catch (err) {
      draftLookupError = err instanceof ApiError
        ? `${err.code}: ${err.message}`
        : err instanceof Error
        ? err.message
        : 'draft_resume_failed';
    } finally {
      draftPromptBusy = false;
    }
  }

  async function handleDiscard(): Promise<void> {
    if (!pendingDraft) return;
    draftPromptBusy = true;
    try {
      await api.discardDraft(pendingDraft.draft_slug);
      pendingDraft = null;
      // The fresh slug minted at onMount stays active; the operator
      // continues editing against that branch. Do not call beginEditing
      // again — it would mint a third slug and orphan the fresh one.
    } catch (err) {
      draftLookupError = err instanceof ApiError
        ? `${err.code}: ${err.message}`
        : err instanceof Error
        ? err.message
        : 'draft_discard_failed';
    } finally {
      draftPromptBusy = false;
    }
  }

  onMount(() => {
    if (!canEditAny) return;
    const resumed = loadFromSession(jurisdiction, product);
    if (resumed) return;
    // Mint a fresh draft slug synchronously so the operator can start
    // editing immediately while detectExistingDraft (a slow listDrafts
    // round-trip — GitHub branch enumeration can take 20+ seconds)
    // runs in the background. The slug-uniqueness fix (defaultDraftSlug
    // YYYYMMDDhhmmss + random suffix) makes the fresh slug
    // collision-resistant against any existing draft branch, so this
    // mint cannot reuse the abandoned draft's branch by accident.
    beginEditing(jurisdiction, product);
    // Advisory prompt only — never blocks editing. If the operator
    // chooses Resume, adoptDraftFromServer overwrites the just-minted
    // store with the server draft's state (sets state.draftSlug to the
    // server slug; the fresh slug's branch is orphaned but harmless
    // because nothing was written to it yet). If they choose Discard,
    // the OLD server draft is archived and the fresh slug stays active.
    void detectExistingDraft();
  });

  const stagedCube = $derived(
    catalogue && Object.keys(stagedEditsState.edits).length > 0
      ? applyStagedEditsToCatalogue(catalogue, stagedEditsState.edits)
      : null
  );

  // hMoney applies a _loan_amount_max_override (data/dimensions/channels.json)
  // that caps every hMoney cube cell's max_loan_amount. The product-level
  // loan_amount_range.max is unaffected — it still governs hLoan. Surface
  // the override as an info note above the Loan amount range section so a
  // proposer reading top-down sees the actual offerable maximum on hMoney.
  const channelOverride = $derived<number | null>(
    dimensions.channels[0]?.values.find((c) => c.id === channel)?._loan_amount_max_override ?? null
  );
  const showChannelMaxOverride = $derived(
    channelOverride !== null && catalogue !== null && channelOverride < catalogue.loan_amount_range.max
  );
</script>

<section>
  {#if !descriptor}
    <p class="banner banner-fail">Unknown product: {jurisdiction}/{product}</p>
  {:else if !catalogue}
    <p class="muted">Loading {descriptor.label}…</p>
  {:else}
    {#if pendingDraft}
      <DraftResumePrompt
        draft={pendingDraft}
        onResume={handleResume}
        onDiscard={handleDiscard}
        busy={draftPromptBusy}
      />
    {/if}
    {#if draftLookupError}
      <p class="banner banner-fail">Draft lookup error: {draftLookupError}</p>
    {/if}
    <header class="detail-header">
      <div>
        <h2>{descriptor.label} — {descriptor.customerLabel}</h2>
        <p class="muted">
          {descriptor.cubeShapeLabel}
          · In Effect <strong>{inEffect?.catalogue_version ?? '—'}</strong>
          since <strong>{formatLocalDate(inEffect?.effective_from.local)}</strong>
        </p>
      </div>
    </header>

    <section aria-labelledby="sec-rate-cube" class="detail-section">
      <h3 id="sec-rate-cube" class="section-heading">Rate cube</h3>
      <div class="rate-cube-controls">
        <label>
          <span class="muted">Channel</span>
          <select bind:value={channel}>
            <option value="hLoan">hLoan</option>
            <option value="hMoney">hMoney</option>
          </select>
        </label>
      </div>
      <div class="detail-grid">
        <div class="detail-grid__cube">
          {#if canEdit}
            <CubeEditor {catalogue} {channel} />
          {:else}
            <CubeTable {catalogue} {channel} />
            <p class="muted">Your role does not include proposer:rates. Editing is hidden; reach an administrator to request the role if you need to propose rate changes.</p>
          {/if}
        </div>
        <aside class="detail-grid__preview">
          <MarketingSurfacePreview {catalogue} {stagedCube} />
        </aside>
      </div>
    </section>

    <section aria-labelledby="sec-amounts" class="detail-section">
      <h3 id="sec-amounts" class="section-heading">Loan amount range</h3>
      {#if showChannelMaxOverride}
        <p class="info-badge" role="note">
          On <strong>{channel}</strong>, the channel override caps Max loan amount at <strong>${formatAmount(channelOverride)}</strong> across every cube cell (source: <code>data/dimensions/channels.json</code>). The product-level Max of <strong>${formatAmount(catalogue.loan_amount_range.max)}</strong> still governs <strong>hLoan</strong>.
        </p>
      {/if}
      {#if canEditAmounts}
        <LoanAmountRangeEditor {catalogue} />
      {:else}
        <ul class="kv-list">
          <li><span class="muted">Min loan amount</span><span class="num">${formatAmount(catalogue.loan_amount_range.min)}</span></li>
          <li><span class="muted">Max loan amount</span><span class="num">${formatAmount(catalogue.loan_amount_range.max)}</span></li>
          <li><span class="muted">Increment</span><span class="num">${formatAmount(catalogue.loan_amount_range.increment)}</span></li>
        </ul>
        <p class="muted">Your role does not include proposer:amounts. Editing is hidden; reach an administrator to request the role if you need to propose amount-range changes.</p>
      {/if}
    </section>

    <section aria-labelledby="sec-terms" class="detail-section">
      <h3 id="sec-terms" class="section-heading">Available terms</h3>
      {#if canEditTerms}
        <TermsEditor {catalogue} />
      {:else}
        <ul class="kv-list">
          <li><span class="muted">Available terms (months)</span><span>{catalogue.terms_months.join(', ')}</span></li>
        </ul>
        <p class="muted">Your role does not include proposer:terms. Editing is hidden; reach an administrator to request the role if you need to propose available-term changes.</p>
      {/if}
    </section>

    <section aria-labelledby="sec-fees" class="detail-section">
      <h3 id="sec-fees" class="section-heading">Fees</h3>
      {#if canEditFees}
        <FeesEditor {catalogue} />
      {:else if catalogue.fees}
        <p class="muted">Your role does not include proposer:fees. Editing is hidden; reach an administrator to request the role if you need to propose fee-schedule changes.</p>
      {/if}
    </section>

    <section aria-labelledby="sec-purposes" class="detail-section">
      <h3 id="sec-purposes" class="section-heading">Loan purposes</h3>
      {#if canEditPurposes}
        <LoanPurposesEditor {catalogue} />
      {:else if (catalogue.loan_purposes?.length ?? 0) > 0}
        <ul class="kv-list">
          {#each catalogue.loan_purposes ?? [] as purpose (purpose.id)}
            <li>
              <span class="muted"><code>{purpose.id}</code> {purpose.display_name}</span>
              <span class="num">
                {purpose.amount_range
                  ? `$${formatAmount(purpose.amount_range.min)} – $${formatAmount(purpose.amount_range.max)}`
                  : '—'}
              </span>
            </li>
          {/each}
        </ul>
        <p class="muted">Your role does not include proposer:purposes. Editing is hidden; reach an administrator to request the role if you need to propose loan-purpose changes.</p>
      {:else}
        <p class="muted">No loan purposes defined for this product.</p>
      {/if}
    </section>
  {/if}
</section>

<style>
  .detail-header {
    display: flex;
    justify-content: space-between;
    align-items: flex-end;
    gap: var(--space-4);
    margin-bottom: var(--space-4);
  }
  /* Per INTERFACE-REVIEW.md §5 R3, each Detail-page heading-and-content
     block is a <section aria-labelledby> with a soft border. The border
     is the load-bearing affordance: it communicates that controls
     inside the section apply inside the section and nowhere else
     (e.g. the Channel selector inside Rate cube, not page-wide). */
  .detail-section {
    border: 1px solid var(--surface-line-soft);
    border-radius: var(--radius-md);
    background: var(--surface-card);
    padding: var(--space-4);
    margin-bottom: var(--space-5);
  }
  .detail-section:last-of-type {
    margin-bottom: 0;
  }
  .detail-section > .section-heading:first-child {
    margin-top: 0;
  }
  .rate-cube-controls {
    display: flex;
    gap: var(--space-3);
    margin-bottom: var(--space-3);
  }
  .info-badge {
    background: var(--accent-info-bg);
    border-left: 3px solid var(--accent-info);
    color: var(--surface-fg);
    padding: var(--space-2) var(--space-3);
    border-radius: var(--radius-sm);
    margin: 0 0 var(--space-3) 0;
    font-size: 0.9375rem;
  }
  .info-badge code {
    font-family: var(--font-mono);
    font-size: 0.875em;
  }
  .detail-grid {
    display: grid;
    grid-template-columns: minmax(0, 1fr);
    gap: var(--space-5);
  }
  @media (min-width: 1280px) {
    .detail-grid {
      grid-template-columns: minmax(0, 1.6fr) minmax(280px, 1fr);
    }
  }
  .kv-list {
    list-style: none;
    padding: 0;
    margin: 0;
    display: grid;
    grid-template-columns: repeat(2, 1fr);
    gap: var(--space-2);
  }
  .kv-list li {
    display: flex;
    justify-content: space-between;
    padding: var(--space-1) 0;
    border-bottom: 1px solid var(--surface-line-soft);
  }
  .num {
    font-family: var(--font-mono);
    font-variant-numeric: tabular-nums;
  }
</style>
